/** Utility for getting the time type from a clock. */
export type ClockTimeType<C extends AnyClock> = ReturnType<C["now"]>;

/** Utility for getting the duration type from a clock. */
export type ClockDurationType<C extends AnyClock> = ReturnType<C["duration"]>;

/** Accepts any type of clock. */
export type AnyClock = Clock<unknown, unknown>;

/** An interface for representing a clock.
 *
 * T = type of a moment in time
 * D = type of a duration of time. */
export abstract class Clock<T, D = number> {
  /** Get the current time. */
  abstract now(): T;

  /** Get the time from a date. */
  abstract fromDate(date: Date): T;

  /** Get the time as a date. */
  abstract toDate(time: T): Date;

  /** Get a moment the given duration after a point in time. */
  abstract add(time: T, duration: D): T;

  /** Get a moment in time the given duration after right now. */
  addToNow(duration: D): T {
    return this.add(this.now(), duration);
  }

  /** Get the duration from one time to another. */
  abstract duration(from: T, to: T): D;

  /** Get the duration of time from now to the given time. */
  durationTill(to: T): D {
    return this.duration(this.now(), to);
  }

  /** Check if some point in time is before another. */
  abstract isBefore(maybeEarlier: T, time: T): boolean;

  /** Check if some point in time is after another. */
  isAfter(maybeLater: T, time: T): boolean {
    return this.isBefore(time, maybeLater);
  }

  /** Check if the given time is in the past. */
  isBeforeNow(time: T): boolean {
    return this.isBefore(time, this.now());
  }

  /** Check if the given time is in the future. */
  isAfterNow(time: T): boolean {
    return this.isAfter(time, this.now());
  }

  /** Convert number of seconds to the duration type. */
  abstract fromSeconds(seconds: number): D;

  /** Convert a duration to a number of seconds. */
  abstract toSeconds(duration: D): number;
}

/** A clock that uses the Date object for time. */
export class DateClock extends Clock<Date> {
  now(): Date {
    return new Date();
  }

  fromDate(date: Date) {
    return new Date(date);
  }

  toDate(time: Date): Date {
    return new Date(time);
  }

  add(time: Date, duration: number): Date {
    return new Date(time.getTime() + duration);
  }

  addToNow(duration: number): Date {
    return new Date(Date.now() + duration);
  }

  duration(from: Date, to: Date): number {
    return to.getTime() - from.getTime();
  }

  durationTill(to: Date): number {
    return to.getTime() - Date.now();
  }

  isBefore(maybeEarlier: Date, time: Date): boolean {
    return maybeEarlier < time;
  }

  isAfter(maybeLater: Date, time: Date): boolean {
    return maybeLater > time;
  }

  fromSeconds(seconds: number): number {
    return seconds * 1000;
  }

  toSeconds(duration: number): number {
    return duration / 1000;
  }
}

/** A clock that uses performance.now for time. */
export class PerformanceClock extends Clock<number> {
  now(): number {
    return performance.now();
  }

  fromDate(date: Date): number {
    const start = Date.now() - performance.now();
    return date.getTime() - start;
  }

  toDate(time: number): Date {
    const start = Date.now() - performance.now();
    return new Date(start + time);
  }

  add(time: number, duration: number): number {
    return time + duration;
  }

  duration(from: number, to: number): number {
    return to - from;
  }

  isBefore(maybeEarlier: number, time: number): boolean {
    return maybeEarlier < time;
  }

  isAfter(maybeLater: number, time: number): boolean {
    return maybeLater > time;
  }

  fromSeconds(seconds: number): number {
    return seconds * 1000;
  }

  toSeconds(duration: number): number {
    return duration / 1000;
  }
}
